import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  MessageSquare, Send, User, Users, Search, X, ArrowLeft, 
  Check, CheckCheck, Clock, Loader2, HeartHandshake, Paperclip,
  Image, File, Download, Wifi, WifiOff, Plus, Trash2
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { messagingAPI } from '../services/api';
import { toast } from 'sonner';
import useChatWebSocket from '../hooks/useChatWebSocket';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generic Chat Interface component that can be used by Worker, Customer, and Owner
const ChatInterface = ({ 
  user, 
  userRole = 'worker', // 'worker', 'customer', 'owner'
  onStartSupportChat,
  onStartGroupChat,
  showGroupChatOption = false,
  height = "600px"
}) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // WebSocket handlers
  const handleNewMessage = useCallback((message) => {
    if (selectedConversation && message.conversation_id === selectedConversation.id) {
      setMessages(prev => {
        // Avoid duplicates
        if (prev.some(m => m.id === message.id)) return prev;
        return [...prev, message];
      });
    }
    // Refresh conversations to update unread counts
    fetchConversations();
  }, [selectedConversation]);

  const handleTyping = useCallback((data) => {
    // Handle typing indicator - can be extended
  }, []);

  const handleStopTyping = useCallback((data) => {
    // Handle stop typing
  }, []);

  const handleRead = useCallback((data) => {
    // Handle read receipts
  }, []);

  const { sendMessage: wsSendMessage, isConnected, subscribe, unsubscribe, markAsRead } = useChatWebSocket(
    handleNewMessage,
    handleTyping,
    handleStopTyping,
    handleRead
  );

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
      if (isConnected) {
        subscribe(selectedConversation.id);
        markAsRead(selectedConversation.id);
      }
    }
    return () => {
      if (selectedConversation && isConnected) {
        unsubscribe(selectedConversation.id);
      }
    };
  }, [selectedConversation?.id, isConnected]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await messagingAPI.getConversations();
      setConversations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await messagingAPI.getConversation(conversationId);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Failed to load messages');
    }
  };

  // Delete conversation (moves to archive)
  const handleDeleteConversation = async (conversationId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Delete this conversation? It will be moved to archive.')) return;
    
    try {
      await messagingAPI.deleteConversation(conversationId);
      toast.success('Conversation archived');
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        setMessages([]);
      }
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };

  const handleStartSupportChat = async () => {
    try {
      const response = await messagingAPI.startSupportChat();
      const conversation = response.data;
      setConversations(prev => {
        const exists = prev.find(c => c.id === conversation.id);
        if (exists) return prev;
        return [conversation, ...prev];
      });
      setSelectedConversation(conversation);
    } catch (error) {
      toast.error('Failed to start support chat');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    const messageContent = newMessage.trim();
    setNewMessage('');
    setSending(true);

    try {
      const response = await messagingAPI.sendMessage(selectedConversation.id, messageContent);
      
      // Add message to local state immediately
      setMessages(prev => [...prev, response.data]);
      
      // Send via WebSocket for real-time
      if (isConnected) {
        wsSendMessage(selectedConversation.id, messageContent);
      }
    } catch (error) {
      toast.error('Failed to send message');
      setNewMessage(messageContent); // Restore message
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedConversation) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('conversation_id', selectedConversation.id);

      const response = await messagingAPI.uploadChatFile(formData);
      
      // Add the file message to the conversation
      if (response.data.message) {
        setMessages(prev => [...prev, response.data.message]);
      }
      
      toast.success('File sent successfully');
      fetchMessages(selectedConversation.id);
    } catch (error) {
      console.error('File upload failed:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getParticipantName = (conversation) => {
    if (!conversation.participants) return conversation.user_name || 'Unknown';
    if (conversation.conversation_type === 'group') {
      return conversation.group_name || 'Group Chat';
    }
    const other = conversation.participants.find(p => p !== user?.id);
    if (conversation.participant_names && other) {
      return conversation.participant_names[other] || 'User';
    }
    return conversation.user_name || 'Support';
  };

  const getParticipantInitials = (conversation) => {
    const name = getParticipantName(conversation);
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getConversationIcon = (conversation) => {
    if (conversation.conversation_type === 'group') {
      return <Users className="w-5 h-5 text-white" />;
    }
    if (conversation.conversation_type === 'support') {
      return <HeartHandshake className="w-5 h-5 text-white" />;
    }
    return null;
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const name = getParticipantName(conv).toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  const renderMessageContent = (msg) => {
    // Check if message has attachment/media
    if (msg.message_type === 'image' || msg.file_type?.startsWith('image/')) {
      return (
        <div className="space-y-2">
          <img 
            src={msg.file_url?.startsWith('http') ? msg.file_url : `${API_URL}${msg.file_url}`}
            alt="Shared image" 
            className="max-w-full rounded-lg cursor-pointer hover:opacity-90"
            onClick={() => window.open(msg.file_url?.startsWith('http') ? msg.file_url : `${API_URL}${msg.file_url}`, '_blank')}
          />
          {msg.content && <p className="text-sm whitespace-pre-wrap">{msg.content}</p>}
        </div>
      );
    }
    
    if (msg.message_type === 'file' || msg.file_url) {
      return (
        <div className="space-y-2">
          <a 
            href={msg.file_url?.startsWith('http') ? msg.file_url : `${API_URL}${msg.file_url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-2 bg-[var(--background-paper)]/10 rounded-lg hover:bg-[var(--background-paper)]/20 transition-colors"
          >
            <File className="w-5 h-5" />
            <span className="text-sm truncate">{msg.file_name || 'Download File'}</span>
            <Download className="w-4 h-4 ml-auto" />
          </a>
          {msg.content && <p className="text-sm whitespace-pre-wrap">{msg.content}</p>}
        </div>
      );
    }
    
    return <p className="text-sm whitespace-pre-wrap">{msg.content}</p>;
  };

  return (
    <div style={{ height }} className="flex rounded-xl overflow-hidden border border-[var(--border-primary)] bg-[var(--background-paper)]" data-testid="chat-interface">
      {/* Conversation List */}
      <div className={`w-full md:w-80 border-r border-[var(--border-primary)] flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
        {/* Header */}
        <div className="p-4 border-b border-[var(--border-primary)] bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)]">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Messages
          </h2>
          <div className="mt-2 flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-red-400'}`} />
            <span className="text-xs text-white/80">{isConnected ? 'Live' : 'Connecting...'}</span>
          </div>
        </div>

        {/* Search */}
        <div className="p-3 border-b border-[var(--border-primary)]">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 text-sm"
              data-testid="chat-search"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p-3 border-b border-[var(--border-primary)] space-y-2">
          {userRole !== 'owner' && (
            <Button 
              onClick={handleStartSupportChat}
              className="w-full bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-white"
              size="sm"
              data-testid="start-support-chat"
            >
              <HeartHandshake className="w-4 h-4 mr-2" />
              Chat with Support
            </Button>
          )}
          {showGroupChatOption && onStartGroupChat && (
            <Button 
              onClick={onStartGroupChat}
              variant="outline"
              className="w-full"
              size="sm"
              data-testid="start-group-chat"
            >
              <Users className="w-4 h-4 mr-2" />
              Create Group Chat
            </Button>
          )}
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--primary)]" />
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="p-8 text-center text-[var(--text-muted)]">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No conversations yet</p>
              <p className="text-sm mt-1">Start a chat to begin messaging</p>
            </div>
          ) : (
            filteredConversations.map(conv => (
              <div
                key={conv.id}
                className={`p-3 border-b border-[var(--border-primary)] hover:bg-[var(--background-subtle)] transition-colors ${
                  selectedConversation?.id === conv.id ? 'bg-[var(--primary)]/10' : ''
                }`}
                data-testid={`conversation-${conv.id}`}
              >
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setSelectedConversation(conv)}
                    className="flex-1 flex items-center gap-3 text-left"
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${
                      conv.conversation_type === 'group' 
                        ? 'bg-[var(--secondary)]' 
                        : 'bg-[var(--primary)]'
                    } text-white`}>
                      {getConversationIcon(conv) || getParticipantInitials(conv)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium truncate">{getParticipantName(conv)}</p>
                        <span className="text-xs text-[var(--text-muted)]">
                          {formatTime(conv.last_message_time || conv.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-[var(--text-muted)] truncate">
                        {conv.last_message || 'No messages yet'}
                      </p>
                    </div>
                  </button>
                  {conv.unread_count > 0 && (
                    <span className="w-5 h-5 bg-[var(--primary)] text-white text-xs rounded-full flex items-center justify-center">
                      {conv.unread_count}
                    </span>
                  )}
                  {/* Delete Button */}
                  <button
                    onClick={(e) => handleDeleteConversation(conv.id, e)}
                    className="p-1.5 text-[var(--error)] hover:bg-[var(--error)]/10 rounded-full transition-colors opacity-60 hover:opacity-100"
                    title="Delete conversation"
                    data-testid={`delete-conversation-${conv.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col ${!selectedConversation ? 'hidden md:flex' : 'flex'}`}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-[var(--border-primary)] bg-[var(--background-subtle)] flex items-center gap-3">
              <button
                onClick={() => setSelectedConversation(null)}
                className="md:hidden p-1 hover:bg-[var(--background-subtle)] rounded"
                data-testid="back-to-list"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                selectedConversation.conversation_type === 'group' 
                  ? 'bg-[var(--secondary)]' 
                  : 'bg-[var(--primary)]'
              } text-white`}>
                {getConversationIcon(selectedConversation) || getParticipantInitials(selectedConversation)}
              </div>
              <div className="flex-1">
                <p className="font-semibold">{getParticipantName(selectedConversation)}</p>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-[var(--text-muted)]">
                    {selectedConversation.conversation_type === 'support' ? 'Support Chat' : 
                     selectedConversation.conversation_type === 'booking' ? 'Booking Chat' : 
                     selectedConversation.conversation_type === 'group' ? `${selectedConversation.participants?.length || 0} members` :
                     'Direct Message'}
                  </p>
                  <span className={`flex items-center gap-1 text-xs ${isConnected ? 'text-[var(--success)]' : 'text-[var(--warning)]'}`}>
                    {isConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                    {isConnected ? 'Live' : 'Offline'}
                  </span>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[var(--background-subtle)]">
              {messages.length === 0 ? (
                <div className="text-center text-[var(--text-muted)] py-8">
                  <p>No messages yet. Say hello!</p>
                </div>
              ) : (
                messages.map((msg, idx) => {
                  const isOwn = msg.sender_id === user?.id;
                  return (
                    <div
                      key={msg.id || idx}
                      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[75%] ${isOwn ? 'order-2' : ''}`}>
                        {!isOwn && selectedConversation.conversation_type === 'group' && (
                          <p className="text-xs text-[var(--text-muted)] mb-1 px-1">{msg.sender_name}</p>
                        )}
                        <div
                          className={`px-4 py-2 rounded-2xl ${
                            isOwn
                              ? 'bg-[var(--primary)] text-white rounded-br-sm'
                              : 'bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-bl-sm'
                          }`}
                        >
                          {renderMessageContent(msg)}
                        </div>
                        <div className={`flex items-center gap-1 mt-1 text-xs text-[var(--text-muted)] ${isOwn ? 'justify-end' : ''}`}>
                          <span>{formatTime(msg.created_at)}</span>
                          {isOwn && (
                            msg.read_at ? (
                              <CheckCheck className="w-3 h-3 text-[#D4AF37]" />
                            ) : (
                              <Check className="w-3 h-3" />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-4 border-t border-[var(--border-primary)] bg-[var(--background-paper)]">
              <div className="flex items-center gap-2">
                {/* File Upload Button */}
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  data-testid="file-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingFile}
                  className="text-[var(--text-muted)] hover:text-[var(--primary)]"
                  data-testid="attach-file-btn"
                >
                  {uploadingFile ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Paperclip className="w-5 h-5" />
                  )}
                </Button>

                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1"
                  disabled={sending}
                  data-testid="message-input"
                />
                <Button
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  className="bg-[var(--primary)] hover:bg-[var(--primary)]/90"
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-[var(--background-subtle)]">
            <div className="text-center text-[var(--text-muted)]">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="font-medium text-lg">Select a conversation</p>
              <p className="text-sm mt-1">Choose from your existing chats or start a new one</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatInterface;
